import React, { RefObject, useEffect, useState } from 'react';
import FullScreenLoader from '../../../../components/loading/FullScreenLoader';
import {
  fetchPlacasResumoLista,
  fetchReguaAlerta,
  PlacasResumoOrder,
  PlacasResumoCadastroSga,
  fetchContacStatus,
  ReguaAlertaResponse,
} from '../../../../api/contac/contacAnalyticsApi';
// `useAlcanca` já inclui o bypass do ADMIN — o `useIsAdmin` que existia aqui ficou sem uso
// quando a decisão de papel passou a ser por interseção (contac/at-13).
import { useAlcanca, useEhAdmin, useTemPapel } from '../../../../hooks/usePapeis';
import RadarRelacionamentoPanel from '../components/RadarRelacionamentoPanel';
import { useRadarRelacionamento } from '../hooks/useRadarRelacionamento';
import ModalPesquisaPorPlaca from '../../../../components/modal/contac/pesquisa-por-placa/ModalPesquisaPorPlaca';
import ModalPlacaDre from '../../../../components/modal/contac/placa-dre/ModalPlacaDre';
import ModalSelecionarRegionalUnico from '../../../../components/modal/contac/regionais/ModalSelecionarRegionalUnico';
import ModalSelecionarCategoria from '../../../../components/modal/contac/categoria/ModalSelecionarCategorias';
import ModalReportsPlaca from '../../../../components/modal/contac/reports/ModalReportsPlaca';
import FiltroModalPlaca from '../../../../components/modal/contac/filtro/ModalFiltroPlaca';
import ComoFunciona, { ComoFuncionaSecao } from '../../../../components/como-funciona/ComoFunciona';
import '../../../../components/como-funciona/help-circle-btn.css';
import ArrowLeftOrange from '../../../../img/arrow-left-orange.svg';
import ArrowLeftGray from '../../../../img/arrow-left-gray.svg';
import ArrowRightOrange from '../../../../img/arrow-right-orange.svg';
import ArrowRightGray from '../../../../img/arrow-right-gray.svg';
import Plate from '../../../../img/plate.svg';
import { toast } from 'react-toastify';
import { notify } from '../../../../shared/feedback/notify';
import Filter from '../../../../img/filter.svg';
import Search from '../../../../img/search-icon.svg';
import ContacDocumentOrange from '../../../../img/contac_document_orange.svg';
import { DismissableTip } from '../../../../components/tooltip/DismissableTip';
import './ResultadoPorPlaca.css';

interface ResultadoPorPlacaProps {
  sidebarRef?: RefObject<HTMLDivElement>;
}

/** Rótulo que o backend usa na linha que agrupa o movimento financeiro sem placa informada. */
const PLACA_NAO_INFORMADA = 'SEM PLACA DEFINIDA';

type AlertColor = 'VERDE' | 'LARANJA' | 'VERMELHO';
type CorResultado = 'green' | 'red' | 'orange';
type ValorMascarado = number | CorResultado;

const isAlertColor = (v: any): v is AlertColor =>
  v === 'VERDE' || v === 'LARANJA' || v === 'VERMELHO';

const alertToUi = (a?: AlertColor): CorResultado => {
  if (a === 'VERDE') return 'green';
  if (a === 'VERMELHO') return 'red';
  return 'orange';
};

const isCorResultado = (v: any): v is CorResultado =>
  v === 'green' || v === 'red' || v === 'orange';

const toValorMascarado = (v: any, alerta?: any): ValorMascarado => {
  if (v === 'ESCONDIDO') return alertToUi(isAlertColor(alerta) ? alerta : 'LARANJA');
  if (isCorResultado(v)) return v;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

interface Resultado {
  placa: string;
  veiculo: string;
  anoMod: string;
  categoria: string;
  associado: string;
  cidade: string;
  resultado: ValorMascarado;
  /**
   * Em quantas competências do período a placa teve boleto pago, e de quantas.
   *
   * ⚠️ Só chega para o RELACIONAMENTO, e existe porque a COR não responde "pagou?": placa
   * inadimplente não gera entrada nem recebe rateio, então fica em zero — laranja, igual a uma
   * placa equilibrada.
   */
  mesesPagos?: number;
  mesesDoCorte?: number;
  cpf?: string;
  regional?: string;
  cooperativa?: string;
  consultor?: string;
  situacao?: string;
  /** false = placa com movimento financeiro que não existe no cadastro de veículos do SGA */
  cadastroSga: boolean;
  semPlacaInformada: boolean;
}

interface FiltroAplicado {
  startYear: number;
  startMonth: number;
  endYear: number;
  endMonth: number;
  order: 'lucro' | 'prejuizo';
  regional?: string;
  codigoVoluntario?: number;
  categoria?: string;
  associadoNome?: string;
  cpfAssociado?: string;
  baseTag?: string;
  placa?: string;
  resultadoMin?: number;
  resultadoMax?: number;
  cadastroSga?: PlacasResumoCadastroSga;
}

const ResultadoPorPlaca: React.FC<ResultadoPorPlacaProps> = ({ sidebarRef }) => {
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState<string | null>(null);
  const [statusIndisponivel, setStatusIndisponivel] = useState(false);
  const [data, setData] = useState<Resultado[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Sem o carimbo de atualizacao o usuario le dado antigo achando que e o mais recente:
    // o aviso fica na propria tela (permanente) alem do toast, que some em segundos.
    fetchContacStatus()
      .then((s) => setUltimaAtualizacao(s.ultimaAtualizacao))
      .catch(() => {
        setStatusIndisponivel(true);
        notify.warning('Não foi possível verificar a data da última atualização dos dados.', { toastId: 'contac-status' });
      });
  }, []);


  const [isPesquisaPorPlacaOpen, setIsPesquisaPorPlacaOpen] = useState(false);

  const defaultDate = new Date();
  defaultDate.setDate(1);
  defaultDate.setMonth(defaultDate.getMonth() - 2);
  const defaultYear = defaultDate.getFullYear();
  const defaultMonth = defaultDate.getMonth() + 1;

  const PAGE_SIZE_OPTIONS = [10, 20, 30, 50, 100, 500] as const;

  const [startYear, setStartYear] = useState<number>(defaultYear);
  const [startMonth, setStartMonth] = useState<number>(defaultMonth);
  const [endYear, setEndYear] = useState<number>(defaultYear);
  const [endMonth, setEndMonth] = useState<number>(defaultMonth);
  const [order, setOrder] = useState<'lucro' | 'prejuizo'>('lucro');
  const [, setCodigoRegional] = useState<number | undefined>();
  const [limit, setlimit] = useState(10);
  const [limitUi, setLimitUi] = useState(10);

  const [modalPlacaDreAberto, setModalPlacaDreAberto] = useState(false);
  const [placaSelecionada, setPlacaSelecionada] = useState<string | null>(null);
  const [nomeRegional, setNomeRegional] = useState('Selecionar regional');
  const [modalRegionaisAberto, setModalRegionaisAberto] = useState(false);
  const [modalCategoriasAberto, setModalCategoriasAberto] = useState(false);
  const [modalReportsPlacaAberto, setModalReportsPlacaAberto] = useState(false);
  const [categoriasSelecionadas, setCategoriasSelecionadas] = useState<string[]>([]);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const headerScrollRef = React.useRef<HTMLDivElement>(null);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (headerScrollRef.current) {
      headerScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItens, setTotalItens] = useState(1);
  const [, setResultadoTotal] = useState<number>(0);

  const [hasFiltered, setHasFiltered] = useState(false);

  const [rangeMinUi, setRangeMinUi] = useState<number>(0);
  const [rangeMaxUi, setRangeMaxUi] = useState<number>(0);
  const [rangeMin, setRangeMin] = useState<number | undefined>(undefined);
  const [rangeMax, setRangeMax] = useState<number | undefined>(undefined);
  const [rangeTouched, setRangeTouched] = useState(false);

  /**
   * ⚠️ Decide por PAPEL, não por `userType` (at-81, ADR 0044).
   *
   * Até a contac/at-13 esta tela comparava `state.auth.userType === 'RELACIONAMENTO'`, e isso
   * quebrava para quem acumula funções — que é o caso de uso que originou o multipapel. O backend
   * já mascarava (ele usa `temPapel`), então a tela recebia `ESCONDIDO` e renderizava como se
   * fosse número: a legenda das cores sumia e os cartões de resumo mostravam "R$ 0,00" no lugar
   * dos totais removidos. O Redux ainda é a fonte errada por outro motivo (não acompanha o
   * refresh do token — BUG-V49); `useTemPapel` lê o token, como o backend.
   */
  const isRelacionamento = useTemPapel('RELACIONAMENTO');
  // RELACIONAMENTO nunca vê valores — a pesquisa por placa abre um DRE completo, então fica de fora
  const canPesquisarPorPlaca = useAlcanca(['CONTAC']);

  /**
   * A aba visível (decisão do Victor, 05/09/2026). Nasceu para o RELACIONAMENTO.
   *
   * O Radar nasceu como painel acima da tabela e virou aba: a lista de placas é o que a pessoa
   * vem consultar, e o painel a empurrava para baixo da dobra.
   */
  const [abaAtiva, setAbaAtiva] = useState<'PLACAS' | 'RADAR'>('PLACAS');

  /**
   * 🔴 EMBARGO TEMPORÁRIO — quem vê a aba do Radar (contac/at-18, 09/09/2026).
   *
   * Decisão do Victor: a feature ainda não foi apresentada à diretoria e **não pode ficar
   * visível nem utilizável pelo usuário final** — só por administradores, que precisam alcançá-la
   * para validar. Até a v1.9.0 o embargo era feito deixando a entrega fora da `master`, o que
   * travou as três entregas seguintes atrás dela.
   *
   * **Era `isRelacionamento`, e é para isso que volta** quando a aprovação vier — trocar esta
   * linha é o desfazer inteiro no frontend. O backend tem o par disto em
   * `PAPEIS_DO_RADAR_DO_RELACIONAMENTO`, e os dois lados sobem e caem JUNTOS: só a tela deixaria
   * as rotas alcançáveis por fora, e só o backend deixaria a aba na tela devolvendo 403.
   *
   * ⚠️ Não substitui `isRelacionamento` em lugar nenhum além da aba: aquele símbolo governa
   * também o mascaramento dos valores, a coluna Pagamento e a legenda das cores. Trocá-lo
   * quebraria a tela de trabalho do papel, que **não** está embargada.
   */
  const podeVerRadar = useEhAdmin();
  const naAbaDoRadar = podeVerRadar && abaAtiva === 'RADAR';

  // O Radar vive no estado da PÁGINA: a tela troca de conteúdo por `FullScreenLoader` a cada
  // carga, e um componente filho seria desmontado (refazendo a consulta) a cada filtro aplicado.
  const radarRelacionamento = useRadarRelacionamento();
  const {
    carregar: carregarRadar,
    jaCarregou: radarJaCarregou,
    periodoCarregado: periodoCarregadoNoRadar,
  } = radarRelacionamento;

  const [isHelpOpen, setIsHelpOpen] = useState(false);
  // O `?` da legenda das cores: explica a régua DO PERÍODO aplicado (contac/at-13).
  const [isReguaHelpOpen, setIsReguaHelpOpen] = useState(false);
  const [regua, setRegua] = useState<ReguaAlertaResponse | null>(null);
  const [reguaIndisponivel, setReguaIndisponivel] = useState(false);

  useEffect(() => {
    if (!isRelacionamento) return;
    fetchReguaAlerta()
      .then(setRegua)
      .catch(() => {
        // A legenda continua correta (as cores são as mesmas); o que falta é a faixa em reais.
        // Nunca inventar o número: o `?` diz que não conseguiu carregar.
        setReguaIndisponivel(true);
        notify.warning('Não foi possível carregar a régua das cores.', { toastId: 'contac-regua' });
      });
  }, [isRelacionamento]);

  const helpSections: ComoFuncionaSecao[] = [
    {
      titulo: 'O que esta tela mostra',
      conteudo: (
        <p>
          Cada linha é uma <strong>placa</strong> e o que ela movimentou no período escolhido:
          Entradas (o que a placa gerou de receita), Saídas (o que custou) e Resultado
          (Entradas menos Saídas). Os dados vêm do sistema financeiro e são consolidados por mês —
          esta tela lê esse consolidado, não lança nada.
        </p>
      ),
    },
    {
      titulo: 'Período de análise',
      conteudo: (
        <>
          <p>
            O período é escolhido por <strong>mês e ano de início e fim</strong>, no botão de filtros.
            O título da tela mostra sempre qual período está sendo exibido.
          </p>
          <p>
            Só é possível consultar a partir de <strong>janeiro de 2025</strong> e até o
            <strong> fechamento de dois meses atrás</strong> — o mês corrente e o anterior ainda não
            têm dados consolidados, por isso não aparecem para seleção.
          </p>
        </>
      ),
    },
    {
      titulo: 'Busca e filtros',
      conteudo: (
        <p>
          O campo de busca no topo entende sozinho o que você digitou: placa completa, CPF (só
          números) ou nome do associado. Nos filtros ficam o período, a quantidade de itens por
          página e o filtro <strong>Cadastro no SGA</strong>, que permite listar somente as placas
          que estão no cadastro de veículos ou somente as que não estão. Ao aplicar, a lista volta
          para a primeira página.
        </p>
      ),
    },
    {
      titulo: 'Placas fora do cadastro do SGA',
      conteudo: (
        <>
          <p>
            Esta tela lista <strong>todo movimento financeiro do período</strong>, e nem toda placa que
            movimenta valores está no cadastro de veículos do SGA. Quando não está, a coluna
            <strong> Situação</strong> mostra <strong>SEM CADASTRO NO SGA</strong> e as demais colunas
            (veículo, associado, consultor, regional) aparecem como "Não informado" — não é campo que
            deixaram de preencher: não existe cadastro no SGA de onde puxar esses dados.
          </p>
          <p>
            <strong>Na maior parte das vezes isso é esperado, não é problema.</strong> Veículos de
            terceiros não são cadastrados no SGA, mas geram lançamentos que chegam por outras fontes
            (Nasajon, Easy Assist e demais origens financeiras) — e é assim que aparecem aqui. Não
            adianta procurar essas placas no SGA: elas não estão lá por definição.
          </p>
          <p>
            Vale conferir na origem quando a placa <strong>deveria</strong> ser de um associado: aí a
            ausência costuma indicar que ela foi registrada de outra forma no sistema financeiro.
          </p>
          <p>
            A linha <strong>SEM PLACA DEFINIDA</strong> é outra coisa: reúne os lançamentos que chegaram
            do financeiro <strong>sem placa nenhuma informada</strong>.
          </p>
        </>
      ),
    },
    ...(isRelacionamento
      ? [
          {
            titulo: 'Por que você vê cores no lugar dos valores',
            conteudo: (
              <>
                <p>
                  No seu perfil, os valores financeiros ficam ocultos por decisão de negócio. O que
                  importa para o seu acompanhamento é a <strong>saúde</strong> de cada placa, e ela
                  aparece como cor: verde para resultado positivo, laranja para resultado próximo de
                  zero e vermelho para resultado negativo.
                </p>
                <p>
                  A faixa do laranja <strong>acompanha o período escolhido</strong>: quanto mais
                  meses, mais larga ela é. O botão <strong>?</strong> ao lado da legenda mostra a
                  faixa exata do período que está na tela.
                </p>
                <p>
                  Por isso esta tela também não oferece ordenação por lucro ou prejuízo, faixa de
                  valores, relatórios nem o detalhamento da placa — todos revelariam o valor que está
                  oculto.
                </p>
              </>
            ),
          },
          {
            titulo: 'A cor não diz se o associado pagou',
            conteudo: (
              <>
                <p>
                  Esta é a confusão mais fácil de fazer nesta tela. A cor mostra{' '}
                  <strong>se a placa deu lucro</strong> no período. Quem deixa de pagar não gera
                  receita, mas também não recebe a parte dele nas despesas: o resultado fica em{' '}
                  <strong>zero</strong>, ou seja, laranja — igualzinho a uma placa equilibrada.
                </p>
                <p>
                  Quem responde sobre pagamento é a coluna <strong>Pagamento</strong>, que mostra em
                  quantos meses do período aquela placa teve boleto pago ("4 de 6").
                  {podeVerRadar ? (
                    <>
                      {' '}
                      A fila de quem está em atraso fica na aba{' '}
                      <strong>Radar do Relacionamento</strong>.
                    </>
                  ) : null}
                </p>
                <p>
                  E o vermelho, na maioria das vezes, é <strong>evento</strong>: o veículo bateu, a
                  associação pagou o reparo, e o custo apareceu naquele período. Não é falta de
                  pagamento.
                </p>
              </>
            ),
          },
          // A seção do Radar só entra para quem vê a aba (embargo da contac/at-18): descrever
          // para o RELACIONAMENTO um painel que ele não alcança seria pior que omiti-lo — ele
          // procuraria a aba na tela e abriria chamado.
          ...(podeVerRadar
            ? [
                {
                  titulo: 'O Radar do Relacionamento (a aba ao lado de Placas)',
                  conteudo: (
                    <>
                      <p>
                        Mostra quem está com <strong>boleto em aberto e vencido</strong>, separado
                        por há quanto tempo o atraso começou — é a fila de trabalho da equipe. Ele
                        carrega <strong>depois</strong> da lista, porque é apoio: a lista nunca
                        espera por ele.
                      </p>
                      <p>
                        A aba <strong>não muda com os filtros</strong> desta tela do mesmo jeito
                        que a lista. A lista responde "como foi o período que você escolheu"; o
                        Radar responde "quem eu procuro hoje". Os dois têm o próprio botão{' '}
                        <strong>?</strong> com as regras completas.
                      </p>
                    </>
                  ),
                },
              ]
            : []),
        ]
      : [
          {
            titulo: 'Detalhamento e relatórios',
            conteudo: (
              <p>
                Clicar em uma <strong>placa</strong> abre o detalhamento com a composição do resultado.
                Também é possível ordenar por lucro ou prejuízo, restringir por faixa de resultado e
                exportar relatórios pelos botões acima da tabela.
              </p>
            ),
          },
        ]),
  ];

  const [dataResultado, setDataResultado] = useState<{
    totalReceita: ValorMascarado;
    totalSaidas: ValorMascarado;
    resultado: ValorMascarado;
  } | null>(null);

  const BolaResultado: React.FC<{ cor: CorResultado; title?: string }> = ({ cor, title }) => (
    <span
      className={`bola-resultado bola-${cor}`}
      title={title}
      aria-label={title ?? cor}
    />
  );

  const [filtroBuscaAssociado, setFiltroBuscaAssociado] = useState('');

  const [buscaTopo, setBuscaTopo] = useState('');
  const buscaTopoAplicadaRef = React.useRef('');

  const [filtroAplicado, setFiltroAplicado] = useState<FiltroAplicado>({
    startYear,
    startMonth,
    endYear,
    endMonth,
    order,
    categoria: undefined,
    resultadoMin: undefined,
    resultadoMax: undefined,
  });

  // Busca do topo: campo único que decide sozinho entre placa (formato completo), CPF (só dígitos) e nome do associado
  const aplicarBuscaTopo = React.useCallback((raw: string) => {
    const value = String(raw ?? '').trim();
    if (value === buscaTopoAplicadaRef.current) return;
    buscaTopoAplicadaRef.current = value;

    const alnum = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    const isPlaca = /^[A-Z]{3}[0-9][A-Z0-9][0-9]{2}$/.test(alnum);
    const digits = value.replace(/\D/g, '');
    const isCpf = !isPlaca && value !== '' && !/[a-zA-ZÀ-ÿ]/.test(value) && digits.length > 0;

    setFiltroBuscaAssociado(isPlaca ? '' : value);
    setFiltroAplicado((prev) => ({
      ...prev,
      placa: isPlaca ? alnum : undefined,
      associadoNome: isPlaca || isCpf ? undefined : (value || undefined),
      cpfAssociado: isCpf ? digits : undefined,
    }));
    setPage(1);
    setHasFiltered(true);
  }, []);

  const formatPeriodo = (sY: number, sM: number, eY: number, eM: number) => {
    const meses = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
    return `${meses[sM - 1]}/${sY} – ${meses[eM - 1]}/${eY}`;
  };

  const periodoExibido =
    hasFiltered
      ? formatPeriodo(
        filtroAplicado.startYear,
        filtroAplicado.startMonth,
        filtroAplicado.endYear,
        filtroAplicado.endMonth
      )
      : '';

  /**
   * Quantas competências o período aplicado cobre — é o que dimensiona a faixa das cores.
   *
   * A resposta do backend traz `mesesDoCorte` (fonte única da régua); este cálculo é só o
   * fallback para antes da primeira carga, quando o `?` pode ser aberto sem dados na tela.
   */
  const mesesDoPeriodoExibido =
    data[0]?.mesesDoCorte ??
    (hasFiltered
      ? (filtroAplicado.endYear - filtroAplicado.startYear) * 12 +
        (filtroAplicado.endMonth - filtroAplicado.startMonth) +
        1
      : 1);

  /**
   * O INÍCIO do período aplicado, que é o que o Radar do Relacionamento respeita (ADR 0008).
   *
   * ⚠️ Só o início. O fim do filtro NÃO é enviado de propósito: ele para na borda consultável do
   * Contac (dois meses atrás, pela defasagem das views), e o painel lê a tabela de boletos, que é
   * diária. Cortar no fim do filtro zeraria o cartão de quem acabou de deixar de pagar — medido em
   * 05/09/2026, 1.266 associados sumiriam. Sem filtro aplicado, o backend usa os últimos 365 dias.
   */
  const periodoDoRadar = hasFiltered
    ? `${filtroAplicado.startYear}-${String(filtroAplicado.startMonth).padStart(2, '0')}-01`
    : undefined;

  const renderValorOuBola = (v: ValorMascarado, title?: string) => {
    if (isCorResultado(v)) return <BolaResultado cor={v} title={title} />;
    return formatarValor(v);
  };

  const filtrosRangeKey = JSON.stringify({
    startYear: filtroAplicado.startYear,
    startMonth: filtroAplicado.startMonth,
    endYear: filtroAplicado.endYear,
    endMonth: filtroAplicado.endMonth,
    regional: filtroAplicado.regional,
    codigoVoluntario: filtroAplicado.codigoVoluntario,
    categoria: filtroAplicado.categoria,
    baseTag: filtroAplicado.baseTag,
    placa: filtroAplicado.placa,
    associadoNome: filtroAplicado.associadoNome,
    cpfAssociado: filtroAplicado.cpfAssociado,
  });

  useEffect(() => {
    if (!hasFiltered) return;

    let active = true;

    const toISODateLocal = (date: Date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };

    const dataInicio = toISODateLocal(new Date(filtroAplicado.startYear, filtroAplicado.startMonth - 1, 1));
    const dataFim = toISODateLocal(new Date(filtroAplicado.endYear, filtroAplicado.endMonth, 0));

    // Para RELACIONAMENTO os resultados vêm mascarados — não há limites de range a calcular
    if (isRelacionamento) return;

    const loadBounds = async () => {
      try {
        const baseParams = {
          dataInicio,
          dataFim,
          page: 1,
          limit: 1,
          regional: filtroAplicado.regional || undefined,
          codigoVoluntario: filtroAplicado.codigoVoluntario ? String(filtroAplicado.codigoVoluntario) : undefined,
          categoria: filtroAplicado.categoria || undefined,
          baseTag: filtroAplicado.baseTag || undefined,
          placa: filtroAplicado.placa || undefined,
          associadoNome: filtroAplicado.cpfAssociado ? undefined : (filtroAplicado.associadoNome || undefined),
          cpfAssociado: filtroAplicado.cpfAssociado || undefined,
          resultadoMin: undefined,
          resultadoMax: undefined,
        };

        const [maxResp, minResp] = await Promise.all([
          fetchPlacasResumoLista({ ...baseParams, order: 'lucro' as PlacasResumoOrder }),
          fetchPlacasResumoLista({ ...baseParams, order: 'prejuizo' as PlacasResumoOrder }),
        ]);

        if (!active) return;

        const firstNumber = (r: any) => {
          const it = Array.isArray(r?.items) ? r.items[0] : undefined;
          if (!it || it.resultado === 'ESCONDIDO') return NaN;
          const n = Number(it.resultado);
          return Number.isFinite(n) ? n : NaN;
        };

        const maxN = firstNumber(maxResp);
        const minN = firstNumber(minResp);

        const minUi =
          Number.isFinite(minN) && Number.isFinite(maxN)
            ? Math.min(minN, maxN)
            : (Number.isFinite(minN) ? minN : (Number.isFinite(maxN) ? maxN : 0));

        const maxUi =
          Number.isFinite(minN) && Number.isFinite(maxN)
            ? Math.max(minN, maxN)
            : (Number.isFinite(maxN) ? maxN : (Number.isFinite(minN) ? minN : 0));

        setRangeMinUi(minUi);
        setRangeMaxUi(maxUi);

        setRangeTouched(false);
        setRangeMin(undefined);
        setRangeMax(undefined);
      } catch (err) {
        if (!active) return;
        // Com 0/0 o filtro por faixa fica inutilizavel e parece que nao ha nada a filtrar.
        // Mensagem generica de carga: esta tela tambem e usada por quem nao pode ver valores.
        setRangeMinUi(0);
        setRangeMaxUi(0);
        notify.errorFrom(err, 'Não foi possível carregar os limites do filtro. Recarregue a página; se persistir, acione a TI.');
      }
    };

    loadBounds();

    return () => {
      active = false;
    };
    // A dependência proposital é o filtrosRangeKey: ele é o JSON exatamente dos campos
    // de filtroAplicado usados aqui (período, regional, consultor, categoria, base, placa,
    // associado e CPF). Depender do objeto filtroAplicado faria os limites do range serem
    // recalculados também quando o próprio range (resultadoMin/resultadoMax) ou a ordenação
    // mudam, o que reabriria o intervalo que o usuário acabou de escolher. isRelacionamento
    // vem do papel do usuário logado e não muda enquanto a tela está aberta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFiltered, filtrosRangeKey]);

  useEffect(() => {
    if (!hasFiltered) return;

    let active = true;

    const {
      startYear,
      startMonth,
      endYear,
      endMonth,
      order: orderFiltro,
      regional,
      codigoVoluntario,
      categoria,
      baseTag,
      placa,
      associadoNome,
      cpfAssociado,
      resultadoMin,
      resultadoMax,
      cadastroSga,
    } = filtroAplicado as any;

    const orderParam = (orderFiltro ?? 'lucro') as PlacasResumoOrder;

    const toISODateLocal = (date: Date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };

    const dataInicio = toISODateLocal(new Date(startYear, startMonth - 1, 1));
    const dataFim = toISODateLocal(new Date(endYear, endMonth, 0));

    const loadData = async () => {
      try {
        setLoading(true);

        const resp = await fetchPlacasResumoLista({
          dataInicio,
          dataFim,
          page: Number(page) || 1,
          limit: Number(limit) || 10,
          order: orderParam,
          regional: regional || undefined,
          codigoVoluntario: codigoVoluntario || undefined,
          categoria: categoria || undefined,
          baseTag: baseTag || undefined,
          placa: placa || undefined,
          associadoNome: cpfAssociado ? undefined : (associadoNome || undefined),
          cpfAssociado: cpfAssociado || undefined,
          resultadoMin: Number.isFinite(Number(resultadoMin)) ? Number(resultadoMin) : undefined,
          resultadoMax: Number.isFinite(Number(resultadoMax)) ? Number(resultadoMax) : undefined,
          cadastroSga: cadastroSga || undefined,
        });

        if (!active) return;

        const items = Array.isArray(resp?.items) ? resp.items : [];

        const formatted = items.map((it: any) => ({
          placa: it.placa,
          veiculo: it.modelo || 'Não informado',
          anoMod: it.ano_modelo?.toString?.() || '',
          categoria: it.categoria || '',
          associado: it.associado || 'Não informado',
          cidade: it.cidade || 'Não informado',
          resultado: toValorMascarado(it.resultado, it.alerta),
          mesesPagos: typeof it.mesesPagos === 'number' ? it.mesesPagos : undefined,
          mesesDoCorte: typeof it.mesesDoCorte === 'number' ? it.mesesDoCorte : undefined,
          cpf: it.cpf_associado || 'Não informado',
          regional: it.regional || 'Não informado',
          cooperativa: it.cooperativa || 'Não informado',
          consultor: it.consultor || 'Não informado',
          situacao: it.situacao || (it.is_ativa === true ? 'ATIVA' : (it.is_ativa === false ? 'INATIVA' : 'Não informado')),
          cadastroSga: it.cadastro_sga !== false,
          semPlacaInformada: it.placa === PLACA_NAO_INFORMADA,
        }));

        setData(formatted);

        const totalItens = Number(resp?.total) || 0;
        const totalPages = Math.max(1, Math.ceil(totalItens / (Number(limit) || 1)));

        setTotalItens(totalItens);
        setTotalPages(totalPages);

        const totalReceitaFallback = items.reduce((acc: number, it: any) => acc + (Number(it.totaisEntrada) || 0), 0);
        const totalSaidasFallback = items.reduce((acc: number, it: any) => acc + (Number(it.totaisSaida) || 0), 0);
        const resultadoFallback = items.reduce((acc: number, it: any) => acc + (it.resultado === 'ESCONDIDO' ? 0 : (Number(it.resultado) || 0)), 0);

        const totalReceitaNum = Number((resp as any)?.entradasTotais?.valor ?? totalReceitaFallback);
        const totalSaidasNum = Number((resp as any)?.despesasTotais?.valor ?? totalSaidasFallback);

        const resultadoTotalRaw = (resp as any)?.resultadoTotal?.valor ?? resultadoFallback;
        const resultadoTotalNum = resultadoTotalRaw === 'ESCONDIDO' ? 0 : Number(resultadoTotalRaw);
        const resultadoTotalBola = resultadoTotalRaw === 'ESCONDIDO'
          ? alertToUi((resp as any)?.resultadoTotal?.alerta)
          : undefined;

        setResultadoTotal(resultadoTotalNum);

        setDataResultado({
          totalReceita: isRelacionamento ? 'orange' : toValorMascarado(totalReceitaNum),
          totalSaidas: isRelacionamento ? 'orange' : toValorMascarado(totalSaidasNum),
          resultado: resultadoTotalRaw === 'ESCONDIDO' ? (resultadoTotalBola ?? 'orange') : toValorMascarado(resultadoTotalNum),
        });
      } catch (err: any) {
        if (!active) return;
        console.error(err);
        toast.error(err?.message || 'Erro ao carregar dados.');
      } finally {
        if (!active) return;
        setLoading(false);
      }
    };

    loadData();

    return () => {
      active = false;
    };
  }, [hasFiltered, filtroAplicado, page, limit, isRelacionamento]);

  /**
   * O Radar carrega DEPOIS da lista, e nunca a segura.
   *
   * Ele é apoio: quem abre a tela precisa da lista para trabalhar. O disparo espera a primeira
   * carga terminar (`!loading`) e acontece uma vez só — as consultas do painel varrem 1,8 milhão
   * de boletos e não têm relação com o filtro aplicado.
   */
  useEffect(() => {
    // ⚠️ Espera o filtro ser aplicado, e não só a lista terminar: antes disso `periodoDoRadar` é
    // indefinido, e o painel carregaria com o período padrão para ser descartado no instante
    // seguinte — duas varreduras da tabela de boletos para exibir uma.
    // `podeVerRadar` e não `isRelacionamento` (embargo da at-18): quem não vê a aba não pode
    // disparar a consulta dela, que voltaria 403 e ainda gastaria uma varredura de boletos.
    if (!podeVerRadar || loading || !hasFiltered) return;
    // 🔴 Recarrega quando o INÍCIO do período muda (ADR 0008). Não é "a cada filtro": o fim, a
    // ordenação e a busca não mexem na fila de cobrança, e o resultado fica em cache compartilhado
    // por (cartão, início, dia) — reabrir o mesmo período não custa consulta.
    if (radarJaCarregou && periodoDoRadar === periodoCarregadoNoRadar) return;
    void carregarRadar(periodoDoRadar);
  }, [
    podeVerRadar,
    radarJaCarregou,
    loading,
    hasFiltered,
    carregarRadar,
    periodoDoRadar,
    periodoCarregadoNoRadar,
  ]);

  const handleOpenReportsPlaca = () => {
    if (isRelacionamento) return;
    if (!hasFiltered) {
      toast.error('Antes selecione um período nos filtros');
      return;
    }
    setModalReportsPlacaAberto(true);
  };

  const formatarValor = (valor: number) => {
    const formatado = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(Math.abs(valor));
    return valor < 0 ? `R$ -${formatado.replace('R$', '').trim()}` : formatado;
  };

  // Removed unused range and filtering helper functions as they are now managed in the modal

  const openFilterModal = () => {
    setFilterModalOpen(true);
  };

  const descricaoFiltros = [
    filtroAplicado.categoria ? `Categoria: ${filtroAplicado.categoria}` : null,
    filtroAplicado.regional ? `Regional: ${nomeRegional}` : null,
    filtroAplicado.cpfAssociado ? `CPF: ${String(filtroAplicado.cpfAssociado)}` : (filtroAplicado.associadoNome ? `Associado: ${String(filtroAplicado.associadoNome)}` : null)
  ].filter(Boolean).join(" • ");

  if (loading) return <FullScreenLoader />;

  return (
    <div className="Frame1000004012 resultado-por-placa-page">
      <div className="ConteDo">

        {/* Título */}
        <div className="TTuloRecepcao">
          <div className="ChefeDeOficina">
            Contac - Dashboard de Placas
            <button
              type="button"
              className="help-circle-btn"
              title="Como Funciona"
              onClick={() => setIsHelpOpen(true)}
              style={{ marginLeft: 8, verticalAlign: 'middle' }}
            >
              ?
            </button>
            {periodoExibido && (
              <span className="PeriodoTitulo"> {periodoExibido}</span>
            )}

            {descricaoFiltros && (
              <span className="PeriodoTitulo" style={{ marginLeft: 8 }}>
                • {descricaoFiltros}
              </span>
            )}
          </div>
          {ultimaAtualizacao ? (
            <p style={{ fontSize: 12, color: 'var(--tema-texto-3, #888)', margin: '4px 0 0 0' }}>
              Dados atualizados em: {new Date(ultimaAtualizacao).toLocaleString('pt-BR')}
            </p>
          ) : statusIndisponivel ? (
            <p style={{ fontSize: 12, color: 'var(--tema-texto-laranja, #b26a00)', margin: '4px 0 0 0' }}>
              Não foi possível verificar a data da última atualização dos dados.
            </p>
          ) : null}
        </div>

        <div className="VisualizarOrdenar">
          <div className="Frame1000004037" style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'center', gap: 12, display: 'flex' }}>
            <DismissableTip
              tipId="contac_placa_filtros"
              content="Selecione o período desejado para filtrar os dados das placas."
              position="bottom"
            >
              <div className="CtaBig MouseHover" onClick={openFilterModal}>
                <div className="Frame1000003987">
                  <img
                    className="imgIcon"
                    src={Filter}
                    alt="Filtro"
                    width="19.80"
                    height="19.80"
                    style={{ cursor: 'pointer' }}
                  />
                  <div className="ComponentName">Filtros</div>
                </div>
              </div>
            </DismissableTip>
            {!isRelacionamento && (
              <DismissableTip
                tipId="contac_placa_relatorios"
                content="Gere relatórios ou exportações de dados das placas."
                position="bottom"
              >
                <div className="CtaBig MouseHover" onClick={handleOpenReportsPlaca}>
                  <div className="Frame1000003987">
                    <img
                      className="imgIcon"
                      src={ContacDocumentOrange}
                      alt="CSV"
                      width="19.80"
                      height="19.80"
                      style={{ cursor: 'pointer' }}
                    />
                    <div className="ComponentName">Relatórios</div>
                  </div>
                </div>
              </DismissableTip>
            )}

            {canPesquisarPorPlaca && (
              <DismissableTip
                tipId="contac_placa_pesquisa"
                content="Faça uma busca direta detalhada por placa específica."
                position="bottom"
              >
                <div className="CtaBig MouseHover" onClick={() => setIsPesquisaPorPlacaOpen(true)}>
                  <div className="Frame1000003987">
                    <img
                      className="imgIcon"
                      src={Plate}
                      alt="Pesquisa por Placa"
                      width="19.80"
                      height="19.80"
                      style={{ cursor: 'pointer' }}
                    />
                    <div className="ComponentName">Pesquisa por Placa</div>
                  </div>
                </div>
              </DismissableTip>
            )}

            <div className="BuscaTopoPlacas">
              <button
                type="button"
                className="BuscaTopoLupa"
                onClick={() => aplicarBuscaTopo(buscaTopo)}
                aria-label="Buscar"
                title="Buscar"
              >
                <img src={Search} alt="Buscar" width="18" height="18" />
              </button>
              <input
                type="text"
                value={buscaTopo}
                onChange={(e) => setBuscaTopo(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') aplicarBuscaTopo(buscaTopo);
                }}
                placeholder="Buscar por placa ou associado..."
                aria-label="Buscar por placa ou associado"
              />
              {buscaTopo && (
                <button
                  type="button"
                  className="BuscaTopoLimpar"
                  onClick={() => {
                    setBuscaTopo('');
                    aplicarBuscaTopo('');
                  }}
                  aria-label="Limpar busca"
                >
                  &times;
                </button>
              )}
            </div>

          </div>
        </div>

        {/*
          Duas abas para o RELACIONAMENTO (decisão do Victor, 05/09/2026): a lista de placas e o
          Radar. Nasceu como painel acima da tabela e virou aba — a tela ficava alta demais e o
          Radar empurrava para baixo justamente a lista que a pessoa veio consultar.

          O molde é o da Recepção ("Em atendimento"/"Entregues"), classes `recepcao-tab*` — a
          tela já importa `Recepcao.css` para o esqueleto.
        */}
        {podeVerRadar && (
          <div className="recepcao-tabs">
            <button
              type="button"
              className={`recepcao-tab ${abaAtiva === 'PLACAS' ? 'active' : ''}`}
              onClick={() => setAbaAtiva('PLACAS')}
            >
              Placas
            </button>
            <button
              type="button"
              className={`recepcao-tab ${abaAtiva === 'RADAR' ? 'active' : ''}`}
              onClick={() => setAbaAtiva('RADAR')}
            >
              Radar do Relacionamento
            </button>
          </div>
        )}

        {isRelacionamento && abaAtiva === 'PLACAS' && (
          <div className="LegendaCoresRelacionamento">
            <span className="LegendaTitulo">Legenda do resultado:</span>
            <div className="LegendaItems">
              <span className="LegendaBadge badge-lucro">
                <span className="bola-resultado bola-green" /> Positivo (lucro)
              </span>
              <span className="LegendaBadge badge-zero">
                <span className="bola-resultado bola-orange" /> Próximo de zero
              </span>
              <span className="LegendaBadge badge-prejuizo">
                <span className="bola-resultado bola-red" /> Negativo (prejuízo)
              </span>
            </div>
            <button
              type="button"
              className="help-circle-btn"
              title="Como a cor é calculada"
              onClick={() => setIsReguaHelpOpen(true)}
            >
              ?
            </button>
          </div>
        )}

        {/* Apoio, não protagonista: carrega depois da lista e nunca a segura (contac/at-13). */}
        {naAbaDoRadar && (
          <RadarRelacionamentoPanel
            estado={radarRelacionamento}
            periodoCarregado={radarRelacionamento.periodoCarregado}
            // Sem filtro aplicado os CARTÕES não têm janela para consultar: a aba diz isso em vez
            // de abrir vazia (06/09/2026). ⚠️ Não vale para os achados do assistente, que desde a
            // at-82 têm janela própria e carregam independentemente do filtro.
            periodoAplicado={hasFiltered}
            onBuscarPlaca={(placa) => {
              // Voltar para a lista faz parte da ação: o filtro só é visível lá.
              setAbaAtiva('PLACAS');
              setBuscaTopo(placa);
              aplicarBuscaTopo(placa);
            }}
          />
        )}

        {/* ====== Bloco de Resumo Financeiro (acima da tabela) ====== */}
        {/* Fora da aba do Radar: para o ADMIN embargado (at-18), os cartões de valor não têm o que
            fazer sobre o painel de cobrança, que é feito de contagens. */}
        {!isRelacionamento && !naAbaDoRadar && (
          <div className="container-superior-consultor">
            <div className="Frame1-consultor">
              {/* Entradas */}
              <div className="card-financeiro-consultor">
                <div className="card-icone">
                  <img style={{ width: 48, height: 48 }} src={require('../../../../img/contac_arrow_down.svg').default} alt="Seta para baixo" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="card-titulo">{isRelacionamento ? 'Entradas' : 'Entradas'}</div>
                  <div className="card-valor">
                    {renderValorOuBola(dataResultado?.totalReceita ?? 0, 'Entradas')}
                  </div>
                </div>
              </div>

              {/* Saídas */}
              <div className="card-financeiro-consultor">
                <div className="card-icone">
                  <img style={{ width: 48, height: 48 }} src={require('../../../../img/contac_arrow_up.svg').default} alt="Seta para cima" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="card-titulo">Saídas</div>
                  <div className="card-valor">
                    {renderValorOuBola(dataResultado?.totalSaidas ?? 0, 'Saídas')}
                  </div>
                </div>
              </div>

              {/* Resultado */}
              <div className="card-financeiro-consultor resultado">
                <div className="card-icone">
                  <img style={{ width: 48, height: 48 }} src={require('../../../../img/contac_line.svg').default} alt="Linha" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="card-titulo">Resultado</div>
                  <div className="card-valor">
                    {isCorResultado(dataResultado?.resultado) ? (
                      <BolaResultado cor={dataResultado?.resultado as any} title="Resultado" />
                    ) : (
                      <span style={{ color: Number(dataResultado?.resultado ?? 0) < 0 ? 'red' : 'green' }}>
                        {formatarValor(Number(dataResultado?.resultado ?? 0))}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* {isAdmin && (
            <div className="CtaBigContac"><div className="Frame1000003987">
              <div className="ComponentNameContac">Regional:</div>
              <button type="button" onClick={() => setModalRegionaisAberto(true)} className="ComponentNameImgIcon">{nomeRegional}</button>
            </div></div>
          )}

          <div className="CtaBigContac"><div className="Frame1000003987">
            <div className="ComponentNameContac">Consultor:</div>
            <button type="button" onClick={() => setModalVoluntariosAberto(true)} className="ComponentNameImgIcon">{nomeVoluntario}</button>
          </div></div>

          <div className="CtaBigContac"><div className="Frame1000003987">
            <div className="ComponentNameContac">Categorias:</div>
            <button type="button" onClick={() => setModalCategoriasAberto(true)} className="ComponentNameImgIcon">
              {categoriasSelecionadas.length ? `${categoriasSelecionadas.length} selecionada(s)` : 'Selecionar categorias'}
            </button>
          </div></div> */}

        {/* Tabela — escondida quando a aba do Radar está ativa (contac/at-13) */}
        <div
          style={{
            background: 'var(--tema-superficie, white)',
            borderRadius: 16,
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            display: naAbaDoRadar ? 'none' : 'flex',
            width: '100%',
            marginBottom: 60,
          }}
        >

          <div className='ContacTable2'>
            <div
              style={{
                display: 'flex',
                gap: 20,
                alignItems: 'center',
                width: '100%',
                flexWrap: 'wrap',
                paddingLeft: 30,
                paddingBottom: 0,
              }}
            >
              {/* Placas por página */}
              <div className="CtaBigContac2">
                <div className="Frame1000003987">
                  <div className="ComponentNameContac">Placas por página:</div>
                  <select
                    value={limitUi}
                    onChange={(e) => {
                      const newLimit = Number(e.target.value);
                      setLimitUi(newLimit);
                      setlimit(newLimit);
                      setPage(1);
                    }}
                    className="ComponentNameImgIcon"
                  >
                    {PAGE_SIZE_OPTIONS.map((num) => (
                      <option key={num} value={num}>{num}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="PlacasTabelaHeaderScroll" ref={headerScrollRef}>
            <div className="PlacasTabelaContent">
              <div className="Tabela">
                <div className="Item" style={{ backgroundColor: 'var(--tema-fundo-2, #e9e9e9)', borderTopLeftRadius: '20px', borderBottomLeftRadius: '20px' }}>
                  <DismissableTip
                    tipId="contac_placa_tabela_dre"
                    content="Clique na placa de um veículo para abrir a Demonstração de Resultado (DRE) detalhada dele."
                    position="top"
                    disabled={data.length === 0 || isRelacionamento}
                  >
                    <div className="CabecalhoData2">Placa</div>
                  </DismissableTip>
                </div>
                <div className="Item" style={{ backgroundColor: 'var(--tema-fundo-2, #e9e9e9)' }}>
                  <div className="CabecalhoData2">Veículo</div>
                </div>
                <div className="Item" style={{ backgroundColor: 'var(--tema-fundo-2, #e9e9e9)' }}>
                  <div className="CabecalhoData2">Categoria</div>
                </div>
                <div className="Item" style={{ backgroundColor: 'var(--tema-fundo-2, #e9e9e9)' }}>
                  <div className="CabecalhoData2">Consultor</div>
                </div>
                <div className="Item" style={{ backgroundColor: 'var(--tema-fundo-2, #e9e9e9)' }}>
                  <div className="CabecalhoData2">Associado</div>
                </div>
                <div className="Item" style={{ backgroundColor: 'var(--tema-fundo-2, #e9e9e9)' }}>
                  <div className="CabecalhoData2">Regional</div>
                </div>
                <div className="Item" style={{ backgroundColor: 'var(--tema-fundo-2, #e9e9e9)' }}>
                  <div className="CabecalhoData2">Cooperativa</div>
                </div>
                <div className="Item" style={{ backgroundColor: 'var(--tema-fundo-2, #e9e9e9)' }}>
                  <div className="CabecalhoData2">Situação</div>
                </div>
                <div
                  className="Item"
                  style={
                    isRelacionamento
                      ? { backgroundColor: '#e9e9e9' }
                      : { backgroundColor: '#e9e9e9', borderTopRightRadius: '20px', borderBottomRightRadius: '20px' }
                  }
                >
                  <div className="CabecalhoData2">Resultado</div>
                </div>
                {/* A cor responde "deu lucro?"; esta coluna responde "pagou?" (contac/at-13). */}
                {isRelacionamento && (
                  <div className="Item" style={{ backgroundColor: 'var(--tema-fundo-2, #e9e9e9)', borderTopRightRadius: '20px', borderBottomRightRadius: '20px' }}>
                    <div className="CabecalhoData2">Pagamento</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="PlacasTabelaScroll" onScroll={handleScroll}>
            <div className="PlacasTabelaContent">

              {data.map((item, idx) => (
                <div className="Tabela" key={idx}>
                  <div className="Item">
                    <div
                      className="Data link"
                      style={{
                        cursor: isRelacionamento ? 'default' : 'pointer',
                        color: isRelacionamento ? 'var(--tema-texto, #1A1C1B)' : '#EA5D0E',
                        textDecoration: isRelacionamento ? 'none' : 'underline',
                      }}
                      onClick={() => {
                        if (isRelacionamento) return;
                        setPlacaSelecionada(item.semPlacaInformada ? '' : item.placa);
                        setModalPlacaDreAberto(true);
                      }}
                    >
                      {item.placa}
                    </div>
                  </div>
                  <div className="Item"><div className="Data">{item.veiculo}</div></div>
                  <div className="Item"><div className="Data">{item.categoria}</div></div>
                  <div className="Item"><div className="Data">{item.consultor}</div></div>
                  <div className="Item"><div className="Data">{item.associado}</div></div>
                  <div className="Item"><div className="Data">{item.regional}</div></div>
                  <div className="Item"><div className="Data">{item.cooperativa}</div></div>
                  <div className="Item">
                    {/* "Não informado" faz o usuário procurar a placa no SGA achando que falta preencher;
                        o que falta é o cadastro do veículo, e é isso que o selo diz */}
                    {item.semPlacaInformada ? (
                      <div className="Data selo-sem-cadastro" title="Lançamentos que chegaram do financeiro sem placa informada">
                        SEM PLACA INFORMADA
                      </div>
                    ) : item.cadastroSga ? (
                      <div className="Data">{item.situacao}</div>
                    ) : (
                      <div className="Data selo-sem-cadastro" title="Placa com movimento financeiro que não está no cadastro do SGA — esperado em veículos de terceiros">
                        SEM CADASTRO NO SGA
                      </div>
                    )}
                  </div>
                  <div className="Item">
                    <div className="Data" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {isCorResultado(item.resultado) ? (
                        <BolaResultado cor={item.resultado} title="Resultado" />
                      ) : (
                        <span style={{ color: item.resultado < 0 ? 'red' : 'green' }}>
                          {item.resultado.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </span>
                      )}
                    </div>
                  </div>
                  {isRelacionamento && (
                    <div className="Item">
                      {/* "0 de 6" é exatamente o que o relacionamento procura: nunca esconder o
                          zero, e nunca deixar a célula vazia (o usuário leria dado faltando). */}
                      {typeof item.mesesPagos === 'number' && typeof item.mesesDoCorte === 'number' ? (
                        <div
                          className="Data"
                          title={`A placa teve boleto pago em ${item.mesesPagos} de ${item.mesesDoCorte} ${item.mesesDoCorte === 1 ? 'mês' : 'meses'} do período`}
                          style={{ color: item.mesesPagos === 0 ? 'var(--tema-texto-vermelho, #C0392B)' : 'var(--tema-texto, #1A1C1B)' }}
                        >
                          {item.mesesPagos} de {item.mesesDoCorte}
                        </div>
                      ) : (
                        <div className="Data" title="Não foi possível verificar os meses pagos">
                          Não verificado
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>


          {/* Paginação */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', width: '100%', marginTop: '20px' }}>
            {totalItens > 0 && (
              <div style={{ fontSize: '14px', color: 'var(--tema-texto-2, #666)' }}>
                Total de {totalItens} placas
              </div>
            )}
            <div className="PaginaO">
              {/* Botão Anterior */}
              <div
                className={`Frame1000003950 ${page === 1 ? 'disabled' : ''}`}
                onClick={() => {
                  if (page > 1) setPage(page - 1);
                }}
                style={{ cursor: page === 1 ? 'not-allowed' : 'pointer' }}
              >
                <img
                  className="imgIcon"
                  src={page === 1 ? ArrowLeftGray : ArrowLeftOrange}
                  alt="Seta para Esquerda"
                  width="24"
                  height="24"
                />
                <div className="Anterior" style={{ color: page === 1 ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)' }}>
                  Anterior
                </div>
              </div>

              {/* Números das páginas */}
              <div className="Group21">
                <div className="Group20">
                  {(() => {
                    const pages = [];
                    const maxVisiblePages = 10;

                    pages.push(
                      <div
                        key={1}
                        className={`page-number ${page === 1 ? 'active' : ''}`}
                        onClick={() => setPage(1)}
                      >
                        1
                        {page === 1 && <div className="Ellipse14" />}
                      </div>
                    );

                    if (page > maxVisiblePages - 4) {
                      pages.push(<span key="left-ellipsis" className="ellipsis">...</span>);
                    }

                    const startPage = Math.max(
                      2,
                      Math.min(
                        page - Math.floor((maxVisiblePages - 2) / 2),
                        totalPages - (maxVisiblePages - 2)
                      )
                    );
                    const endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 3);

                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <div
                          key={i}
                          className={`page-number ${page === i ? 'active' : ''}`}
                          onClick={() => setPage(i)}
                        >
                          {i}
                          {page === i && <div className="Ellipse14" />}
                        </div>
                      );
                    }

                    if (endPage < totalPages - 1) {
                      pages.push(<span key="right-ellipsis" className="ellipsis">...</span>);
                    }

                    const middlePage = Math.floor(totalPages / 2);
                    if (totalPages > 2) {
                      pages.push(
                        <div
                          key="middle"
                          className={`page-number ${page === middlePage ? 'active' : ''}`}
                          onClick={() => setPage(middlePage)}
                        >
                          {middlePage}
                          {page === middlePage && <div className="Ellipse14" />}
                        </div>
                      );
                    }

                    if (totalPages > 1) {
                      pages.push(
                        <div
                          key={totalPages}
                          className={`page-number ${page === totalPages ? 'active' : ''}`}
                          onClick={() => setPage(totalPages)}
                        >
                          {totalPages}
                          {page === totalPages && <div className="Ellipse14" />}
                        </div>
                      );
                    }

                    return pages;
                  })()}
                </div>
              </div>

              {/* Botão Próxima */}
              <div
                className={`Frame1000003949 ${page === totalPages ? 'disabled' : ''}`}
                onClick={() => {
                  if (page < totalPages) setPage(page + 1);
                }}
                style={{ cursor: page === totalPages ? 'not-allowed' : 'pointer' }}
              >
                <div className="PrXima" style={{ color: page === totalPages ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)' }}>
                  Próxima
                </div>
                <img
                  className="imgIcon"
                  src={page === totalPages ? ArrowRightGray : ArrowRightOrange}
                  alt="Seta para Direita"
                  width="24"
                  height="24"
                />
              </div>
            </div>
          </div>
        </div>
        <ModalPesquisaPorPlaca
          isOpen={isPesquisaPorPlacaOpen}
          onCancel={() => setIsPesquisaPorPlacaOpen(false)}
          sidebarRef={sidebarRef}
        />
        {modalRegionaisAberto && (
          <ModalSelecionarRegionalUnico
            onClose={() => setModalRegionaisAberto(false)}
            onSelect={(codigo, nome) => {
              setCodigoRegional(codigo);
              setNomeRegional(nome);
              setModalRegionaisAberto(false);
            }}
          />
        )}
        {!isRelacionamento && modalPlacaDreAberto && placaSelecionada !== null && (
          <ModalPlacaDre
            isOpen={modalPlacaDreAberto}
            onClose={() => {
              setModalPlacaDreAberto(false);
              setPlacaSelecionada(null);
            }}
            placa={placaSelecionada}
            dataInicio={new Date(startYear, startMonth - 1, 1).toISOString().split('T')[0]}
            dataFim={new Date(endYear, endMonth, 0).toISOString().split('T')[0]}
          />
        )}
        {modalCategoriasAberto && (
          <ModalSelecionarCategoria
            selected={categoriasSelecionadas}
            onClose={() => setModalCategoriasAberto(false)}
            onSelect={(cats: string[]) => {
              setCategoriasSelecionadas(cats);
              setModalCategoriasAberto(false);
            }}
          />
        )}
        <FiltroModalPlaca
          isOpen={filterModalOpen}
          onClose={() => setFilterModalOpen(false)}
          sidebarRef={sidebarRef}
          onApplyFilters={(sY, sM, eY, eM, newOrder, newMin, newMax, touched, searchAssociado, novoCadastroSga) => {
            const value = String(searchAssociado ?? '').trim();
            const digits = value.replace(/\D/g, '');
            const isCpf = value !== '' && !/[a-zA-ZÀ-ÿ]/.test(value) && digits.length > 0;

            setFiltroBuscaAssociado(value);
            setBuscaTopo(value);
            buscaTopoAplicadaRef.current = value;
            setStartYear(sY);
            setStartMonth(sM);
            setEndYear(eY);
            setEndMonth(eM);
            if (newOrder) setOrder(newOrder);
            setRangeTouched(!!touched);
            setRangeMin(newMin);
            setRangeMax(newMax);

            setFiltroAplicado({
              startYear: sY,
              startMonth: sM,
              endYear: eY,
              endMonth: eM,
              order: newOrder || order,
              regional: nomeRegional !== 'Selecionar regional' ? nomeRegional : undefined,
              resultadoMin: touched ? (newMin ?? rangeMinUi) : undefined,
              resultadoMax: touched ? (newMax ?? rangeMaxUi) : undefined,
              associadoNome: isCpf ? undefined : (value || undefined),
              cpfAssociado: isCpf ? digits : undefined,
              cadastroSga: novoCadastroSga as PlacasResumoCadastroSga | undefined,
            });

            setPage(1);
            setHasFiltered(true);
          }}
          initialStartYear={startYear}
          initialStartMonth={startMonth}
          initialEndYear={endYear}
          initialEndMonth={endMonth}
          initialOrder={order}
          initialResultadoMin={rangeMin}
          initialResultadoMax={rangeMax}
          initialRangeTouched={rangeTouched}
          rangeMinUi={rangeMinUi}
          rangeMaxUi={rangeMaxUi}
          initialSearchAssociado={filtroBuscaAssociado}
          initialCadastroSga={filtroAplicado.cadastroSga}
          hideResultadoControls={isRelacionamento}
        />
        {!isRelacionamento && modalReportsPlacaAberto && (
          <ModalReportsPlaca
            isOpen={modalReportsPlacaAberto}
            onClose={() => setModalReportsPlacaAberto(false)}
            initialPlacas={filtroAplicado.placa ? [String(filtroAplicado.placa)] : []}
            baseTag={filtroAplicado.baseTag ?? undefined}
            dataInicio={new Date(startYear, startMonth - 1, 1).toISOString().split('T')[0]}
            dataFim={new Date(endYear, endMonth, 0).toISOString().split('T')[0]}
          />
        )}

        <ComoFunciona
          isOpen={isHelpOpen}
          onClose={() => setIsHelpOpen(false)}
          titulo="Como Funciona — Dashboard de Placas"
          secoes={helpSections}
        />

        {isReguaHelpOpen && (
          <ComoFunciona
            isOpen
            onClose={() => setIsReguaHelpOpen(false)}
            titulo="Como a cor é calculada"
            secoes={secoesDaRegua(regua, reguaIndisponivel, mesesDoPeriodoExibido)}
          />
        )}
      </div>
    </div>
  );
};

/**
 * O `?` da legenda: explica a cor com a faixa DO PERÍODO que está na tela (contac/at-13).
 *
 * A faixa em reais vem do backend (`rPorMes`), nunca de um número escrito aqui: régua duplicada no
 * frontend divergiria da que o sistema aplica no primeiro ajuste. Sem a régua carregada, o texto
 * diz isso — nunca inventa o valor.
 */
function secoesDaRegua(
  regua: ReguaAlertaResponse | null,
  indisponivel: boolean,
  mesesDoPeriodo: number,
): ComoFuncionaSecao[] {
  const banda = regua ? regua.rPorMes * Math.max(1, mesesDoPeriodo) : null;
  const emReais = (v: number) =>
    v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

  return [
    {
      titulo: 'O que a cor responde',
      conteudo: (
        <>
          <p>
            A cor mostra o <strong>resultado da placa no período escolhido</strong>: o que ela pagou
            de mensalidade menos o que custou (a parte dela nas despesas e qualquer custo direto,
            como um reparo).
          </p>
          <p>
            Ela responde <strong>"a placa deu lucro?"</strong>, e não "o associado pagou?". Quem não
            paga não gera entrada e também não recebe parte das despesas: fica em zero, ou seja,
            laranja. Para ver pagamento, use a coluna <strong>Pagamento</strong> e o Radar do topo.
          </p>
        </>
      ),
    },
    {
      titulo: 'A faixa depende de quantos meses você escolheu',
      conteudo: indisponivel ? (
        <p>
          Não foi possível carregar a régua agora, então não dá para mostrar a faixa em reais deste
          período. As cores na tela continuam corretas. Recarregue a página; se persistir, acione a
          TI.
        </p>
      ) : banda === null ? (
        <p>Carregando a régua do período...</p>
      ) : (
        <>
          <p>
            Um mês de uma placa em dia costuma sobrar algumas dezenas de reais. Se a faixa do
            laranja fosse fixa, um período curto pintaria quase tudo de laranja e um período longo
            quase nada. Por isso ela cresce junto com o período.
          </p>
          <p>
            Neste período de <strong>{mesesDoPeriodo} {mesesDoPeriodo === 1 ? 'mês' : 'meses'}</strong>:
            verde acima de <strong>{emReais(banda)}</strong>, vermelho abaixo de{' '}
            <strong>{emReais(-banda)}</strong>, e laranja entre os dois.
          </p>
        </>
      ),
    },
    {
      titulo: 'Vermelho quase sempre é evento, não falta de pagamento',
      conteudo: (
        <p>
          Quando um veículo sofre um evento, a associação paga o reparo e aquela placa fica bem
          negativa no período. Isso é a proteção funcionando. Falta de pagamento aparece como{' '}
          <strong>laranja com a coluna Pagamento baixa</strong>, não como vermelho.
        </p>
      ),
    },
  ];
}

export default ResultadoPorPlaca;
